
import { Component, Ref, Vue, Watch } from 'vue-property-decorator';
import ErrorAlert from 'spa-ionata-admin/lib/components/ErrorAlert.vue';
import { emptyCheck, emailRules } from 'spa-ionata-admin/lib/utils/validators';
import ObservedPromise from 'spa-ionata-admin/lib/services/ObservedPromise';
import AsideLabel from '../components/AsideLabel.vue';
import FormItem, { StepItem } from '../components/FormItem.vue';
import EoiLead from '../models/EoiLead';
import Business, { BusinessProxy } from '../models/Business';

@Component({ components: { AsideLabel, FormItem, ErrorAlert } })
export default class extends Vue {
  @Ref() readonly form!: VuetifyForm;

  data: HashAny = {
    firstName: null,
    lastName: null,
    email: null,
    businessName: null,
    enquiry: null,
  };

  result = new ObservedPromise<boolean>();

  valid = false;

  dataPromise = new ObservedPromise<BusinessProxy>();

  get slug() {
    return this.$route.params.slug;
  }

  get business(): Business | undefined {
    return this.dataPromise.value?.data || undefined;
  }

  get fields(): StepItem[] {
    const $t = this.$t.bind(this) as (key: string) => string;
    const requiredRule = (msg: string) => [(v: string) => emptyCheck(v) || msg];
    const items = [
      {
        type: 'group',
        sm: 6,
        items: [
          {
            key: 'firstName',
            label: `${$t('enquiry.first_name_label')}*`,
            placeholder: `${$t('enquiry.first_name_placeholder')}`,
            input: 'text',
            autofocus: true,
            required: true,
            rules: requiredRule($t('enquiry.first_name_required')),
          },
          {
            key: 'lastName',
            label: `${$t('enquiry.last_name_label')}*`,
            placeholder: `${$t('enquiry.last_name_placeholder')}`,
            input: 'text',
            required: true,
            rules: requiredRule($t('enquiry.last_name_required')),
          },
        ],
      },
      {
        key: 'businessName',
        label: `${$t('enquiry.business_name_label')}*`,
        placeholder: `${$t('enquiry.business_name_placeholder')}`,
        input: 'text',
        required: true,
        rules: requiredRule($t('enquiry.business_name_required')),
      },
      {
        key: 'email',
        label: `${$t('enquiry.email_label')}*`,
        placeholder: `${$t('enquiry.email_placeholder')}`,
        input: 'email',
        required: true,
        rules: this.emailRules,
      },
      {
        type: 'textarea',
        key: 'enquiry',
        label: `${$t('enquiry.enquiry_label')}*`,
        placeholder: `${$t('enquiry.enquiry_placeholder')}`,
        required: true,
        rules: requiredRule($t('enquiry.enquiry_text_required')),
      },
      {
        label: `${$t('general.required_fields_description')}`,
        labelClass: 'caption',
      },
    ];
    return items;
  }

  get emailRules() {
    const translator = (t: string) => this.$t(`general.${t}`) as string;
    return emailRules(translator);
  }

  get loading() {
    return this.result.isPending;
  }

  get success() {
    return this.result.isFulfilled && this.result.value === true;
  }

  get error(): Error | null {
    return this.result.error || this.dataPromise.error || null;
  }

  submit() {
    if (!this.form.validate()) {
      return;
    }
    const { business } = this;
    const { firstName, lastName, email, businessName, enquiry } = this.data;
    const content = enquiry ? { enquiry } : undefined;
    const eoiLead = new EoiLead({
      firstName,
      lastName,
      email,
      businessName,
      content,
      business,
    });
    const promise = eoiLead.save({
      with: ['business'],
    });
    this.result = new ObservedPromise(promise);
  }

  @Watch('slug', { immediate: true })
  onSlugChanged(slug: string | undefined) {
    if (slug) {
      const promise = Business.where({ slug }).first();
      this.dataPromise = new ObservedPromise(promise);
    } else {
      this.dataPromise = new ObservedPromise();
    }
  }
}
